import Guid from "_shared/guids/guid";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import { DocumentFileScanStatus } from "documents/downloads/document-file-scan-status";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import { z } from "zod";

/**
 * The facts a download gate decides on, wherever they come from: a `Document` the documents API sent,
 * or a `DocumentDownloadVerdict` for a row another center projected. `documentDownloadWithheldReason`
 * reads only these, so either will do.
 */
export type DownloadVerdictFields = {
  readonly classificationStatus?: DocumentClassificationStatus;
  readonly contentAccess?: DocumentContentAccess;
  readonly restrictedSubjectMatters?: DocumentSubjectMatter[];
  readonly scanStatus?: DocumentFileScanStatus;
};

const DocumentDownloadVerdictJSONZ = z.object({
  id: z.uuid(),
  classificationStatus: z.enum(DocumentClassificationStatus),
  contentAccess: z.enum(DocumentContentAccess),
  restrictedSubjectMatters: z.array(z.string()).nullish(),
  scanStatus: z.enum(DocumentFileScanStatus),
});

export type DocumentDownloadVerdictJSON = z.infer<typeof DocumentDownloadVerdictJSONZ>;

/**
 * Whether this reader's download of one document would be refused, and why (#6992).
 *
 * What a work or proposal page asks for its rows, from `documents/get-download-verdicts-by-ids`. It
 * names no person: the proposal decides which party may learn who uploaded a document (#6623), and a
 * full `Document` would carry the creator's user id past that decision.
 */
export default class DocumentDownloadVerdict implements DownloadVerdictFields {
  public static readonly JSONZ = DocumentDownloadVerdictJSONZ;

  public static fromJSON(json: unknown): DocumentDownloadVerdict {
    const parsed = parseOrThrow(DocumentDownloadVerdictJSONZ, json, {
      logError: true,
      schemaName: "DocumentDownloadVerdict",
      operation: "fromJSON",
      payloadSample: json,
    });
    return new DocumentDownloadVerdict(parsed);
  }

  public readonly id: Guid;
  public readonly classificationStatus: DocumentClassificationStatus;
  public readonly contentAccess: DocumentContentAccess;
  public readonly restrictedSubjectMatters?: DocumentSubjectMatter[];
  public readonly scanStatus: DocumentFileScanStatus;

  private constructor(json: DocumentDownloadVerdictJSON) {
    this.id = new Guid(json.id);
    this.classificationStatus = json.classificationStatus;
    this.contentAccess = json.contentAccess;
    // A label this build does not know is dropped, as `DocumentSchema` drops one.
    this.restrictedSubjectMatters = json.restrictedSubjectMatters
      ?.map((name) => DocumentSubjectMatter[name as keyof typeof DocumentSubjectMatter])
      .filter((value): value is DocumentSubjectMatter => typeof value === "number");
    this.scanStatus = json.scanStatus;
  }
}
